import { color, spacing, font, media } from "@universityofmaryland/web-token-library";
import "../../typography/font-face/index.js";
import "../../typography/campaign.js";
import "../../typography/elements.js";
import { smaller } from "../../typography/sans.js";
import "../../typography/serif.js";
import "../../typography/stats.js";
import "../../animation/line.js";
import "../../animation/loader.js";
import { linksWhite, linksDark } from "../../animation/nested-elements.js";
import "../../animation/transition.js";
import { objectWithClassName } from "../../utilities/create/jss.js";
import "../../node_modules/.pnpm/postcss@8.5.8/node_modules/postcss/lib/postcss.js";
import "../../node_modules/.pnpm/postcss-js@4.1.0_postcss@8.5.8/node_modules/postcss-js/index.js";
import "../../node_modules/.pnpm/postcss-nesting@13.0.2_postcss@8.5.8/node_modules/postcss-nesting/dist/index.js";
import "../../_virtual/index.js";
import { ordered, unordered } from "../list.js";
import { composeInline } from "../table.js";
import { code } from "./code.js";
import { quote } from "./quote.js";
const classNamePrefix = "umd-text-rich";
const scalingContainerMedium = 650;
({
  ...linksDark,
  color: color.white,
  "& *": {
    color: `${color.white}`
  }
});
const childSpacing = {
  marginTop: spacing.md,
  "&:first-child": {
    marginTop: "0"
  },
  "&:empty": {
    marginTop: 0
  }
};
const largestTypography = {
  fontSize: font.size["2xl"],
  lineHeight: "1.25em"
};
const simpleBase = {
  ...linksWhite,
  fontSize: font.size.base,
  lineHeight: "1.5em",
  "& > *": {
    fontSize: font.size.base,
    ...childSpacing
  },
  "& em, & i": {
    fontStyle: "italic"
  },
  "& strong, & b": {
    FontWeight: font.weight.bold
  },
  "& u": {
    textDecoration: "underline"
  }
};
const advancedBase = {
  ...simpleBase,
  fontSize: font.size.lg,
  lineHeight: "1.5em",
  "& > *": {
    fontSize: font.size.lg,
    ...childSpacing
  },
  [`& p,
    & ul,
    & ol,
    & pre,
    & blockquote`]: {
    maxWidth: "960px"
  },
  "& hr": {
    border: "none",
    height: "1px",
    backgroundColor: "currentColor"
  },
  "& img": {
    maxWidth: "100%"
  },
  "& sup": {
    fontSize: font.size.min
  },
  "& sub": {
    fontSize: font.size.min
  },
  "& small": {
    ...smaller,
    display: "inline-block"
  }
};
function composeSimple(options) {
  const {
    size = "base",
    theme = "light",
    color: explicitColor,
    scaling = false
  } = options || {};
  let composed = {
    ...simpleBase,
    [`& ul, & ol ul`]: {
      ...unordered
    },
    [`& ol, & ul ol`]: {
      ...ordered
    },
    ["& + ol, &ol + ul"]: {
      marginTop: spacing.sm
    }
  };
  if (size === "large") {
    composed = {
      ...composed,
      fontSize: font.size.lg,
      "& > *": {
        ...composed["& > *"],
        fontSize: font.size.lg
      }
    };
  } else if (size === "largest") {
    composed = {
      ...composed,
      fontSize: font.size.lg,
      [`@media (${media.queries.desktop.min})`]: {
        ...largestTypography
      },
      "& > *": {
        ...composed["& > *"],
        fontSize: font.size.lg,
        [`@media (${media.queries.desktop.min})`]: {
          ...largestTypography
        }
      }
    };
  }
  if (scaling) {
    const baseFontSize = size === "base" ? font.size.base : font.size.lg;
    const scaledFontSize = font.size.lg;
    composed = {
      ...composed,
      fontSize: baseFontSize,
      "& > *": {
        ...composed["& > *"],
        fontSize: baseFontSize
      },
      [`@container (min-width: ${scalingContainerMedium}px)`]: {
        fontSize: scaledFontSize,
        "& > *": {
          fontSize: scaledFontSize,
          ...childSpacing
        }
      }
    };
  }
  const finalColor = explicitColor || (theme === "dark" ? color.white : color.gray.dark);
  const useWhiteLinks = finalColor === color.white;
  composed = {
    ...composed,
    color: finalColor,
    "& *": {
      color: finalColor
    },
    // Apply white link styles if color is white
    ...useWhiteLinks ? linksDark : {}
  };
  const classNameParts = [classNamePrefix, "simple"];
  if (size !== "base") classNameParts.push(size);
  if (scaling) classNameParts.push("scaling");
  if (theme === "dark") classNameParts.push("dark");
  const className = classNameParts.join("-");
  const deprecatedAliases = [];
  if (size === "base" && !scaling && theme === "light") {
    deprecatedAliases.push("umd-text-simple");
  }
  if (size === "large" && !scaling && theme === "light") {
    deprecatedAliases.push("umd-text-simple-large");
  }
  return objectWithClassName({
    ...composed,
    className: deprecatedAliases.length > 0 ? [className, ...deprecatedAliases] : className
  });
}
function composeAdvanced(options) {
  const { theme = "light", color: explicitColor } = options || {};
  let composed = {
    FontWeight: font.weight.normal,
    ...advancedBase,
    ...code,
    ...quote,
    [`& ul, & ol ul`]: {
      ...unordered
    },
    [`& ol, & ul ol`]: {
      ...ordered
    },
    "& + ol, &ol + ul": {
      marginTop: spacing.sm
    },
    "& table": {
      ...composeInline({ theme })
    }
  };
  const finalColor = explicitColor || (theme === "dark" ? color.white : color.gray.dark);
  const useWhiteLinks = finalColor === color.white;
  composed = {
    ...composed,
    color: finalColor,
    "& *": {
      color: finalColor
    },
    // Apply white link styles if color is white
    ...useWhiteLinks ? linksDark : {},
    // Additional dark theme styles for tables and lists when using white color
    ...useWhiteLinks && {
      [`& ul, & ol ul`]: {
        ...unordered,
        "& *, & * > *": {
          color: color.white
        }
      },
      [`& ol, & ul ol`]: {
        ...ordered,
        "& *, & * > *": {
          color: color.white
        }
      }
    }
  };
  const className = theme === "dark" ? `${classNamePrefix}-advanced-dark` : `${classNamePrefix}-advanced`;
  const deprecatedAliases = theme === "dark" ? ["umd-rich-text-dark"] : ["umd-rich-text"];
  return objectWithClassName({
    ...composed,
    className: [className, ...deprecatedAliases]
  });
}
const simple = composeSimple();
const simpleDark = composeSimple({ theme: "dark" });
const simpleLarge = composeSimple({ size: "large" });
const simpleLargeDark = composeSimple({
  size: "large",
  theme: "dark"
});
const simpleLargest = composeSimple({ size: "largest" });
const simpleLargestDark = composeSimple({
  size: "largest",
  theme: "dark"
});
const simpleScaling = composeSimple({ scaling: true });
const simpleScalingDark = composeSimple({
  scaling: true,
  theme: "dark"
});
const advanced = composeAdvanced();
const advancedDark = composeAdvanced({ theme: "dark" });
export {
  advanced,
  advancedDark,
  composeAdvanced,
  composeSimple,
  simple,
  simpleDark,
  simpleLarge,
  simpleLargeDark,
  simpleLargest,
  simpleLargestDark,
  simpleScaling,
  simpleScalingDark
};
//# sourceMappingURL=rich.js.map
